(() => {
  if (customElements.get('coverly-cart-viewer')) return;
  const definitions = new Map();
  let active = null;
  const getJSON = async (url, signal) => {
    const response = await fetch(url, { credentials:'same-origin', signal });
    if (!response.ok) throw new Error('Unavailable');
    return response.json();
  };
  function endpoint(path, values) {
    const url = new URL('/apps/coverly/' + path, location.origin);
    for (const [key, value] of Object.entries(values)) if (value) url.searchParams.set(key, value);
    return url;
  }
  function definition(element, fresh = false) {
    const url = endpoint('configurator', {
      shopify_product_id:element.dataset.product,
      shopify_variant_id:element.dataset.variant,
      country:window.CoverlyCart?.country || '',
    });
    const key = url.href, cached = definitions.get(key);
    if (!fresh && cached && cached.until > Date.now()) return cached.promise;
    const promise = getJSON(url, AbortSignal.timeout(15000));
    definitions.set(key, { promise, until:Date.now() + 60000 });
    promise.catch(() => { if (definitions.get(key)?.promise === promise) definitions.delete(key); });
    return promise;
  }
  function productValue(setting, value) {
    if (setting.type === 'number') return typeof value === 'number' && Number.isFinite(value);
    if (setting.type === 'boolean') return typeof value === 'boolean';
    const values = setting.type === 'strings' ? value : [value];
    return Array.isArray(values) && values.length <= 200 && values.every(v => typeof v === 'string' && v.length <= 200 && (!setting.choices?.length || setting.choices.some(c => c.value === v)));
  }
  function mappedValues(connection, values, productId) {
    const result = Object.create(null);
    for (const setting of connection.settings) {
      const mapping = connection.mappings.find(m => m.target === setting.key);
      let value = null;
      if (setting.source === 'product') {
        value = connection.products?.find(p => p.productId === String(productId).replace('gid://shopify/Product/', ''))?.values[setting.key] ?? null;
        if (value !== null && !productValue(setting, value)) value = null;
      } else if (mapping) {
        const raw = values[mapping.source];
        if (raw != null && String(raw).trim() !== '') {
          const convert = v => Object.prototype.hasOwnProperty.call(mapping.choices || {}, v) ? mapping.choices[v] : v;
          if (setting.type === 'strings') value = (Array.isArray(raw) ? raw : String(raw).split(',')).map(v => convert(String(v)));
          else {
            const v = convert(String(raw));
            if (setting.type === 'number') {
              value = Number(v) * (mapping.scale ?? 1) + (mapping.offset ?? 0);
              if (!Number.isFinite(value)) value = null;
            } else if (setting.type === 'boolean') value = v === 'true' ? true : v === 'false' ? false : null;
            else value = v.length <= 200 ? v : null;
          }
          if (Array.isArray(value) && (value.length > 200 || value.some(v => v.length > 200))) value = null;
          if (setting.choices?.length && (Array.isArray(value) ? value : [value]).some(v => !setting.choices.some(c => c.value === v))) value = null;
          if (value === null) throw new Error('Invalid configuration');
        }
      }
      if (value === null && setting.required) throw new Error('Incomplete configuration');
      result[setting.key] = value;
    }
    return result;
  }
  function signature(settings) {
    return JSON.stringify(settings.map(s => ({ key:s.key, type:s.type, source:s.source, required:!!s.required, choices:(s.choices || []).map(c => c.value) })));
  }
  function visibleValues(def, config) {
    const values = Object.create(null), dimensions = Object.create(null), blocked = new Set();
    for (const [key, value] of Object.entries({ ...config.selections, ...config.dimensions })) {
      values[key] = String(value ?? '');
      if (values[key].trim() !== '' && !isNaN(Number(values[key]))) dimensions[key] = Number(values[key]);
    }
    const matches = (conditions, combinator) => {
      if (!conditions?.length) return false;
      const results = conditions.map(c => {
        if (Array.isArray(c.conditions)) return matches(c.conditions, c.combinator || 'and');
        const at = key => key in dimensions ? dimensions[key] : values[key];
        const number = key => Number.isFinite(Number(at(key))) ? Number(at(key)) : 0;
        const sum = keys => keys.reduce((total, key) => total + number(key), 0);
        const left = c.plus?.length ? number(c.option_key) + sum(c.plus) : at(c.option_key);
        const right = c.value_of?.length ? sum(c.value_of) * (Number.isFinite(Number(c.factor)) ? Number(c.factor) : 1) : c.value;
        switch (c.operator) {
          case 'empty': return left == null || String(left).trim() === '';
          case 'not_empty': return left != null && String(left).trim() !== '';
          case 'eq': return left === right;
          case 'neq': return left !== right;
          case 'in': return Array.isArray(right) && right.includes(left);
          case 'nin': return Array.isArray(right) && !right.includes(left);
          case 'gt': return Number(left) > Number(right);
          case 'gte': return Number(left) >= Number(right);
          case 'lt': return Number(left) < Number(right);
          case 'lte': return Number(left) <= Number(right);
          default: return false;
        }
      });
      return combinator === 'or' ? results.some(Boolean) : results.every(Boolean);
    };
    const seed = (type, object) => { if (['hidden', 'disabled'].includes(object.visibility)) blocked.add(type + ':' + object.id); };
    for (const section of def.sections) {
      seed('section', section);
      for (const option of section.options) {
        seed('option', option);
        for (const choice of option.choices) seed('choice', choice);
      }
    }
    for (const rule of def.visibility || []) {
      if (!matches(rule.conditions, rule.combinator)) continue;
      const key = rule.target_type + ':' + rule.target_id;
      if (rule.action === 'show') blocked.delete(key); else blocked.add(key);
    }
    const allowed = Object.create(null);
    for (const section of def.sections) {
      if (blocked.has('section:' + section.id)) continue;
      for (const option of section.options) {
        if (blocked.has('option:' + option.id) || !['single_select', 'multi_select', 'number_dropdown', 'number', 'range'].includes(option.input_type)) continue;
        const raw = values[option.key] || '';
        allowed[option.key] = option.choices.length ? raw.split(',').filter(value => option.choices.some(c => c.value === value && !blocked.has('choice:' + c.id))).join(',') : raw;
      }
    }
    return allowed;
  }
  function eligible(def, element) {
    return def?.viewer?.enabled && Array.isArray(def.viewer.settings) && Array.isArray(def.viewer.mappings) && String(def.shopify_product_id) === element.dataset.product;
  }
  function open(element) {
    active?.close();
    const trigger = element.querySelector('button'), drawer = element.closest('cart-drawer');
    const labels = element.dataset, row = element.closest('.cart-item');
    const dialog = document.createElement('dialog');
    dialog.className = 'coverly-cart-3d';
    dialog.setAttribute('aria-labelledby', 'coverly-cart-3d-title');
    dialog.setAttribute('aria-describedby', 'coverly-cart-3d-help');
    dialog.innerHTML = '<header class="coverly-cart-3d__head"><div><h2 id="coverly-cart-3d-title"></h2><p></p></div><button type="button" data-close></button></header><div class="coverly-cart-3d__body"><div class="coverly-cart-3d__stage"></div><div class="coverly-cart-3d__status" role="status"></div></div><footer class="coverly-cart-3d__footer"><p id="coverly-cart-3d-help"></p><button type="button" data-retry hidden></button></footer>';
    const stage = dialog.querySelector('.coverly-cart-3d__stage');
    const status = dialog.querySelector('[role=status]');
    const retryButton = dialog.querySelector('[data-retry]');
    const closeButton = dialog.querySelector('[data-close]');
    dialog.querySelector('h2').textContent = labels.room.trim() || labels.title;
    const dimensions = row.querySelector('.coverly-cart-summary__dimensions')?.cloneNode(true);
    dimensions?.querySelector('strong')?.remove();
    dialog.querySelector('header p').textContent = [labels.title, dimensions?.textContent.trim()].filter(Boolean).join(' · ');
    dialog.querySelector('footer p').textContent = labels.help;
    closeButton.textContent = labels.close;
    retryButton.textContent = labels.retry;
    let frame, channel, origin, render, ready = false, sequence = 0, abort, retry, timeout;
    const revision = 1;
    function preventDrawerEscape(event) {
      if (event.key === 'Escape' || event.code === 'Escape') event.stopImmediatePropagation();
    }
    document.addEventListener('keyup', preventDrawerEscape, true);
    function teardown() {
      sequence++; clearInterval(retry); clearTimeout(timeout);
      abort?.abort(); abort = null;
      frame?.remove(); frame = null; stage.replaceChildren(); ready = false;
    }
    function fail() {
      teardown(); status.hidden = false; status.removeAttribute('data-loading');
      status.textContent = labels.error; retryButton.hidden = false;
    }
    function send(type, data = {}) {
      frame?.contentWindow.postMessage({ protocol:'ppo.viewer', version:1, channel, type, ...data }, origin);
    }
    function arm(run) {
      clearTimeout(timeout); timeout = setTimeout(() => { if (run === sequence) fail(); }, 30000);
    }
    async function load() {
      teardown(); const run = sequence;
      status.hidden = false; status.dataset.loading = ''; status.textContent = labels.loading; retryButton.hidden = true;
      abort = new AbortController(); arm(run);
      try {
        const [config, def] = await Promise.all([
          getJSON(endpoint('config', { uid:labels.config }), abort.signal),
          definition(element, true),
        ]);
        if (run !== sequence || !dialog.open) return;
        if (!eligible(def, element) || config.product_config_id !== def.product_config_id || String(config.shopify_product_id) !== labels.product) throw new Error('Mismatched product');
        render = { settings:def.viewer.settings, values:mappedValues(def.viewer, visibleValues(def, config), def.shopify_product_id) };
        const url = new URL(def.viewer.url);
        if (url.protocol !== 'https:' || url.username || url.password || url.origin === location.origin) throw new Error('Invalid viewer');
        url.searchParams.set('embed', '1'); url.searchParams.set('quote_view', '1');
        origin = url.origin;
        frame = document.createElement('iframe'); frame.hidden = true;
        frame.title = (labels.room.trim() || labels.title) + ' — ' + labels.title;
        frame.setAttribute('sandbox', 'allow-scripts allow-same-origin'); frame.referrerPolicy = 'no-referrer';
        frame.addEventListener('load', () => {
          if (run !== sequence) return;
          frame.hidden = true; ready = false; status.hidden = false;
          channel = crypto.randomUUID(); clearInterval(retry); arm(run);
          send('init'); retry = setInterval(() => send('init'), 500);
        });
        frame.src = url.href; stage.appendChild(frame);
      } catch (_) { if (run === sequence) fail(); }
    }
    function message(event) {
      const d = event.data;
      if (!frame || !dialog.open || event.source !== frame.contentWindow || event.origin !== origin || d?.protocol !== 'ppo.viewer' || d.version !== 1 || d.channel !== channel) return;
      if (d.type === 'dismiss') { dialog.close(); return; }
      if (d.type === 'ready' && !ready) {
        try { if (signature(d.settings) !== signature(render.settings)) return fail(); } catch (_) { return fail(); }
        ready = true; clearInterval(retry); send('configure', { revision, values:render.values });
      }
      if (!ready || d.revision !== revision) return;
      if (d.type === 'error') return fail();
      if (d.type === 'applied') { clearTimeout(timeout); frame.hidden = false; status.hidden = true; }
    }
    closeButton.addEventListener('click', () => dialog.close());
    retryButton.addEventListener('click', load);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      teardown(); window.removeEventListener('message', message); window.removeEventListener('pagehide', dismiss);
      setTimeout(() => document.removeEventListener('keyup', preventDrawerEscape, true), 300);
      dialog.remove(); if (active === dialog) active = null;
      if (drawer?.classList.contains('active')) drawer.syncModalFocus();
      if (trigger.isConnected) trigger.focus({ preventScroll:true });
    });
    const dismiss = () => dialog.close();
    window.addEventListener('message', message); window.addEventListener('pagehide', dismiss);
    document.body.appendChild(dialog); active = dialog; dialog.showModal();
    drawer?.syncModalFocus(); closeButton.focus(); load();
  }
  class CoverlyCartViewer extends HTMLElement {
    connectedCallback() {
      this.fitLabel = () => {
        if (!this.isConnected || !this.clientWidth) return;
        const button = this.querySelector('button'), label = this.querySelector('[data-viewer-label]');
        const style = getComputedStyle(button);
        const width = label.getBoundingClientRect().width + button.querySelector('svg').getBoundingClientRect().width + parseFloat(style.columnGap) + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        button.toggleAttribute('data-compact', width > button.clientWidth);
      };
      this.labelObserver = new ResizeObserver(this.fitLabel);
      this.labelObserver.observe(this);
      this.labelObserver.observe(this.querySelector('[data-viewer-label]'));
      this.check = async () => {
        if (!this.isConnected || !this.closest('cart-drawer')?.classList.contains('active') || this.checking) return;
        this.checking = true;
        try {
          const def = await definition(this);
          if (this.isConnected) this.hidden = !eligible(def, this);
        } catch (_) { this.hidden = true; }
        finally { this.checking = false; }
      };
      this.click = () => open(this);
      this.addEventListener('click', this.click);
      document.addEventListener('cart-drawer:open', this.check);
      this.check();
    }
    disconnectedCallback() {
      this.labelObserver?.disconnect();
      this.removeEventListener('click', this.click);
      document.removeEventListener('cart-drawer:open', this.check);
    }
  }
  customElements.define('coverly-cart-viewer', CoverlyCartViewer);
})();
